import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import tinycolor from "tinycolor2";

import type { RemoteSelectionData } from "../types";

/** Metadata key for updating remote selections via transaction. */
export const UPDATE_REMOTE_SELECTIONS_META = "updateRemoteSelections";

/** Creates an inline decoration for highlighting the full selection range. */
function createSelectionRangeDecoration(
  from: number,
  to: number,
  color: string,
  srcPresenceId: string,
): Decoration | null {
  if (from === to) {
    return null;
  }

  return Decoration.inline(
    from,
    to,
    {
      style: `background-color: ${tinycolor(color).setAlpha(0.3).toString()}`,
    },
    {
      key: `${srcPresenceId}-selection`,
    },
  );
}

/**
 * Creates a widget decoration for a cursor at the given position.
 * @param username - The shortened username to display in the label
 * @param srcPresenceId - The ShareDB presence ID to use as the widget key
 * @param fadeLabelTimeouts - Map tracking active fade timeouts per presence ID
 */
function createSelectionHeadWidget(
  pos: number,
  color: string,
  userId: string,
  username: string,
  srcPresenceId: string,
  isFirstRender: boolean,
  fadeLabelTimeouts: Map<string, ReturnType<typeof setTimeout>>,
): Decoration {
  return Decoration.widget(
    pos,
    () => {
      const container = document.createElement("span");
      container.className = "remote-cursor-container";
      container.style.setProperty("--cursor-color", color);
      container.setAttribute("data-user-id", userId);

      const cursor = document.createElement("span");
      cursor.className = "remote-cursor";
      container.appendChild(cursor);

      // Check if there's an active fade timeout - if so, show label even if not first render
      const hasActiveFadeTimeout = fadeLabelTimeouts.has(srcPresenceId);
      const shouldShowLabel = isFirstRender || hasActiveFadeTimeout;

      const flag = document.createElement("span");
      flag.className = "remote-cursor-flag";
      container.appendChild(flag);

      const label = document.createElement("div");
      label.className = "remote-cursor-label";
      label.style.opacity = shouldShowLabel ? "1" : "0";
      label.textContent = username;
      container.appendChild(label);

      const fadeLabel = () => {
        label.classList.add("fade-slow");
        label.style.opacity = "0";
        fadeLabelTimeouts.delete(srcPresenceId);
      };

      const showLabelOnHover = () => {
        const existingTimeout = fadeLabelTimeouts.get(srcPresenceId);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
          fadeLabelTimeouts.delete(srcPresenceId);
        }
        label.classList.remove("fade-slow");
        label.style.opacity = "1";
        const timeout = setTimeout(fadeLabel, 3000);
        fadeLabelTimeouts.set(srcPresenceId, timeout);
      };

      // Show label on hover of cursor or label
      cursor.addEventListener("mouseenter", showLabelOnHover);
      label.addEventListener("mouseenter", showLabelOnHover);

      if (shouldShowLabel) {
        const timeout = setTimeout(fadeLabel, 3000);
        fadeLabelTimeouts.set(srcPresenceId, timeout);
      }

      return container;
    },
    {
      key: srcPresenceId,
    },
  );
}

/** Creates a plugin that uses ProseMirror's Decoration system to display cursors and selections from other users. */
export function remoteSelectionsPlugin(): Plugin<RemoteSelectionData[]> {
  // Use a static plugin key since each editor instance has its own EditorState
  const pluginKey = new PluginKey<RemoteSelectionData[]>("remoteSelections");

  // Tracks active fade timeouts per presence ID across widget recreations for this plugin instance
  const fadeLabelTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

  return new Plugin<RemoteSelectionData[]>({
    key: pluginKey,

    state: {
      init() {
        return [];
      },

      apply(tr, value) {
        // Check if this transaction is updating remote selections
        const updateData = tr.getMeta(UPDATE_REMOTE_SELECTIONS_META) as
          | RemoteSelectionData[]
          | undefined;
        if (updateData !== undefined) {
          return updateData;
        }
        return value;
      },
    },

    props: {
      decorations(state) {
        const remoteSelections = pluginKey.getState(state) || [];

        if (remoteSelections.length === 0) {
          return DecorationSet.empty;
        }

        const decorations: Decoration[] = [];

        for (const selection of remoteSelections) {
          // Add cursor widget at selection head
          decorations.push(
            createSelectionHeadWidget(
              selection.to,
              selection.color,
              selection.userId,
              selection.username,
              selection.srcPresenceId,
              selection.isFirstRender,
              fadeLabelTimeouts,
            ),
          );

          const rangeDecoration = createSelectionRangeDecoration(
            selection.from,
            selection.to,
            selection.color,
            selection.srcPresenceId,
          );
          if (rangeDecoration) {
            decorations.push(rangeDecoration);
          }
        }

        return DecorationSet.create(state.doc, decorations);
      },
    },
  });
}
